import logo from "@/assets/root-logo.png";
import { COGNITO_CLIENT_ID, COGNITO_USER_POOL_ID } from "@/constants";
import { Authenticator, Heading, Image, useTheme, View } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import { fetchAuthSession, resetPassword, signIn, signUp } from "aws-amplify/auth";
import axios from "axios";
import React from "react";
import "./CustomAuthenticator.scss";

interface NewComponentProps {
  children: React.ReactElement;
}

Amplify.configure({
  ...{
    aws_project_region: "eu-central-1",
    aws_cognito_region: "eu-central-1",
    aws_user_pools_id: COGNITO_USER_POOL_ID,
    aws_user_pools_web_client_id: COGNITO_CLIENT_ID,
  },
});

interface ErrorMap {
  [key: string]: string;
}

const ErrorMaps: ErrorMap = {
  "PreSignUp failed with error Email address already registered.":
    "Email address already registered. Error code: 102",
  "User already exists": "Email address already registered. Error code: 103",
  "UserMigration failed with error Incorrect username or password.":
    "Incorrect username or password.",
  "UserMigration failed with error Email not found.": "Invalid Email address",
};

const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View
        textAlign="center"
        padding={tokens.space.large}
      >
        <Image
          alt="Amplify logo"
          src={logo}
          width="200px"
          height="auto"
        />
      </View>
    );
  },
  ConfirmSignUp: {
    Header() {
      return (
        <Heading
          // padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          style={{ fontWeight: "bold" }}
          level={3}
        >
          We Emailed You
        </Heading>
      );
    },
  },
  SignIn: {
    Footer() {
      // hide forgot password
      return <></>;
    },
  },
};

const services = {
  async handleSignUp(input: any) {
    let { username, options } = input;

    let userAttributes = {
      email: username.toLowerCase(),
      "custom:self_sign_up": "true",
      "custom:first_name": options["userAttributes"]["custom:first_name"],
      "custom:last_name": options["userAttributes"]["custom:last_name"],
      "custom:company_name": options["userAttributes"]["custom:company_name"],
    };

    const first_name = options["userAttributes"]["custom:first_name"];
    const last_name = options["userAttributes"]["custom:last_name"];
    const company_name = options["userAttributes"]["custom:company_name"];

    return signUp({
      username,
      options: {
        userAttributes: userAttributes,
        validationData: {
          self_sign_up: "1",
          first_name: first_name,
          last_name: last_name,
          company_name: company_name,
        },
        autoSignIn: {
          authFlowType: "USER_AUTH",
        },
      },
    })
      .then((data) => {
        return data;
      })
      .catch((error) => {
        throw ErrorMaps[error.message] || error;
      });
  },
  async handleSignIn(input: any) {
    const { username } = input;
    return signIn({
      username: username.toLowerCase(),
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge: "EMAIL_OTP",
      },
    })
      .then((data) => {
        return data;
      })
      .catch((error) => {
        throw ErrorMaps[error.message] || error;
      });
  },
  async handleForgotPassword(email: any) {
    return resetPassword(email)
      .then((data) => {
        return data;
      })
      .catch((error) => {
        console.log(error.message, ErrorMaps[error.message]);
        throw ErrorMaps[error.message] || error;
      });
  },
};

const formFields = {
  signUp: {
    "custom:first_name": {
      label: "First Name:",
      placeholder: "Enter your First Name:",
      isRequired: true,
      order: 1,
    },
    "custom:last_name": {
      label: "Last Name:",
      placeholder: "Enter your Last Name:",
      isRequired: true,
      order: 2,
    },
    email: {
      label: "Email:",
      placeholder: "Enter your Email:",
      isRequired: true,
      order: 3,
    },
    "custom:company_name": {
      label: "Company name:",
      order: 5,
      isRequired: true,
    },
    password: {
      labelHidden: true,
      label: "Password:",
      placeholder: "Enter your Password:",
      isRequired: false,
      order: 6,
      type: "hidden",
      style: { display: "none" },
    },
    confirm_password: {
      labelHidden: true,
      label: "Confirm Password:",
      isRequired: false,
      order: 7,
      type: "hidden",
      style: { display: "none" },
    },
  },
  signIn: {
    password: {
      labelHidden: true,
      isRequired: false,
      order: 1,
      type: "hidden",
      style: { display: "none" },
    },
  },
};

const CustomAuthenticator: React.FC<NewComponentProps> = ({ children }) => {
  const path = window.location.pathname;
  const initialState = path === "/signup" ? "signUp" : "signIn";

  return (
    <Authenticator
      loginMechanisms={["email"]}
      components={components}
      formFields={formFields}
      services={services}
      initialState={initialState}
    >
      {({ user }) => {
        axios.interceptors.request.use(
          async (config) => {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            config.headers["Authorization"] = `Bearer ${token}`;

            return config;
          },
          (error) => Promise.reject(error)
        );

        return React.cloneElement(children, { user: user });
      }}
    </Authenticator>
  );
};

export default CustomAuthenticator;
